import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Plus, Lock, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, brl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ComandasManager() {
  const { user } = useAuth();
  const isAdmin = (user?.perms || []).includes("excluir");
  const [comandas, setComandas] = useState([]);
  const [products, setProducts] = useState([]);
  const [novo, setNovo] = useState({ name: "", phone: "", user_id: "" });
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/comandas", { params: { status: "aberta" } });
      setComandas(res.data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/bar/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers([...r.data].filter((u) => u.name).sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })))).catch(() => {});
  }, [load]);

  const pickUser = (id) => {
    const u = users.find((x) => x.user_id === id);
    setNovo(u ? { user_id: id, name: u.name, phone: u.phone || "" } : { name: "", phone: "", user_id: "" });
  };

  const criar = async () => {
    if (!novo.user_id && !novo.name.trim()) return toast.error("Selecione um usuário ou digite o nome do cliente.");
    try { await api.post("/admin/comandas", novo); setNovo({ name: "", phone: "", user_id: "" }); await load(); toast.success("Comanda aberta!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addItem = async (cid) => {
    const pid = pick[cid];
    if (!pid) return toast.error("Escolha um produto.");
    try { await api.post(`/admin/comandas/${cid}/items`, { product_id: pid, qty: 1 }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fechar = async (cid) => {
    try { await api.post(`/admin/comandas/${cid}/close`); await load(); toast.success("Comanda fechada e registrada como venda!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const excluir = async (cid) => {
    if (!window.confirm("Excluir comanda?")) return;
    try { await api.delete(`/admin/comandas/${cid}`); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-8" data-testid="comandas-manager">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Comandas abertas</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">Abra uma conta por cliente e vá adicionando produtos; feche para registrar a venda.</p>

      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3">
        <select data-testid="comanda-user" value={novo.user_id} onChange={(e) => pickUser(e.target.value)} className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">Selecionar usuário do app (A-Z)... ou digite abaixo</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}{u.phone ? ` — ${u.phone}` : ""}</option>)}
        </select>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <Input data-testid="comanda-name" disabled={!!novo.user_id} placeholder="Nome do cliente" value={novo.name} onChange={(e) => setNovo({ ...novo, name: e.target.value })} className="rounded-xl" />
          <Input data-testid="comanda-phone" disabled={!!novo.user_id} placeholder="Telefone" value={novo.phone} onChange={(e) => setNovo({ ...novo, phone: e.target.value })} className="rounded-xl" />
          <Button data-testid="comanda-create" onClick={criar} className="rounded-xl bg-[#1B7A45] text-white hover:bg-[#156337]"><Plus className="mr-1 h-4 w-4" /> Abrir comanda</Button>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {comandas.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-8 text-center text-sm text-slate-400">Nenhuma comanda aberta.</p> :
          comandas.map((c) => (
            <div key={c.id} data-testid={`comanda-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div><p className="font-display font-bold text-slate-900">{c.customer_name}</p><p className="text-xs text-slate-500">{c.customer_phone}</p></div>
                <span className="font-display text-xl font-black text-[#1B7A45]">{brl(c.total)}</span>
              </div>
              {c.items.length > 0 && <p className="mt-2 text-xs text-slate-600">{c.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</p>}
              <p data-testid={`comanda-time-${c.id}`} className="mt-1 text-[11px] text-slate-400">Aberta em {new Date(c.created_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select data-testid={`comanda-pick-${c.id}`} value={pick[c.id] || ""} onChange={(e) => setPick({ ...pick, [c.id]: e.target.value })} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                  <option value="">Produto...</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {brl(p.price)}</option>)}
                </select>
                <Button data-testid={`comanda-additem-${c.id}`} onClick={() => addItem(c.id)} size="sm" className="rounded-full bg-[#E8611A] text-white hover:bg-[#CF5212]"><Plus className="h-4 w-4" /></Button>
                <Button data-testid={`comanda-close-${c.id}`} onClick={() => fechar(c.id)} size="sm" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"><Lock className="mr-1 h-4 w-4" /> Fechar</Button>
                {isAdmin && <Button data-testid={`comanda-del-${c.id}`} onClick={() => excluir(c.id)} size="sm" variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
